import React, { useCallback, useRef, useState } from 'react';
import { ApolloError, FetchResult, useMutation, useQuery } from '@apollo/client';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Box, CircularProgress, Typography } from '@material-ui/core';

import {
  GET_PUBLIC_EDIT_PLAYER,
  PLAYER_FIELDS,
  PLAYER_MILESTONE_FIELDS,
  RECORD_TOKEN_PLAYER_SESSION,
} from '../../../lib/queries/player.queries';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { SharedPlayerProfileFields } from '../../Player/SharedPlayerProfileFields/shared-player-profile-fields.component';
import { PlayerMilestoneSection } from '../../Player/PlayerMilestoneHistory/player-milestone-section.component';
import { HANDEDNESS_LABELS } from '../../../lib/types';
import { buildFieldsAction, buildMilestoneAction } from '../../../lib/helpers/player-action.helper';
import { dateOfBirthSchema } from '../../../lib/helpers/player.validation';
import { findAck, isAppliedAck } from '../../../lib/helpers/user-action-ack.helpers';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { FormHelperText, IFormOption } from '../../_core/_ui/forms.component';
import { useRecordPlayerEdit } from '../../../lib/services/player-session.service';
import { PlayerPageCard } from './player-page-card.component';

interface IPublicEditPlayerProfileProps {
  code: string;
  playerAssessmentId: string;
  token: string | null;
}

interface IPublicProfileFields {
  firstName: string;
  lastName: string;
  nickname: string;
  date: string;
  handedness?: IFormOption | null;
}

const FORM_ID = 'public-edit-player-profile-form';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('Enter a first name'),
  lastName: Yup.string().required('Enter a last name'),
  date: dateOfBirthSchema('Enter a date of birth'),
});

// Profile editor reachable two ways: anonymous URL token (email link) OR a
// logged-in PARENT JWT that owns the player. The credential picks the wire
// path — token path uses recordTokenUserSession; parent path uses the shared
// recordUserSession buffer via useRecordPlayerEdit.
export const PublicEditPlayerProfile: React.FC<IPublicEditPlayerProfileProps> = ({ code, playerAssessmentId, token }) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);

  // Stable per-mount timestamp so this page's field save and milestone records
  // group into one server UserSession (keyed on actor + startedAt).
  const sessionStartedAt = useRef(new Date()).current;

  const useTokenPath = !!token;

  const { data } = useQuery(GET_PUBLIC_EDIT_PLAYER, {
    variables: { code, token },
    fetchPolicy: 'cache-and-network',
  });

  // Token path: direct token-scoped mutation (no IUser to key the offline buffer).
  // Parent path: shared user-session buffer (parent JWT identifies the actor).
  // Every mutation refetches BOTH the editor's source query AND the page header
  // — per "Mutations refresh every affected view" in REACT_CONVENTIONS.md.
  const PROFILE_REFETCH_QUERIES = ['GetPublicEditPlayer', 'GetPublicPlayerHeader'];
  const [updatePlayer] = useMutation(RECORD_TOKEN_PLAYER_SESSION, {
    refetchQueries: PROFILE_REFETCH_QUERIES,
  });
  const [recordMilestone] = useMutation(RECORD_TOKEN_PLAYER_SESSION, {
    refetchQueries: PROFILE_REFETCH_QUERIES,
  });
  const recordPlayerEdit = useRecordPlayerEdit(PROFILE_REFETCH_QUERIES);

  const player: IPlayer | undefined = data && data.player;

  const handleSubmit = useCallback(async (values: IPublicProfileFields, { setSubmitting }: FormikHelpers<IPublicProfileFields>) => {
    if (!player) return undefined;
    setSubmitError(null);
    const fields = {
      firstName: values.firstName,
      lastName: values.lastName,
      nickname: values.nickname,
      dateOfBirth: values.date,
      handedness: values.handedness?.value,
    };
    const action = buildFieldsAction(player._id, playerAssessmentId, fields);

    const onSucceeded = () => {
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    };
    // The server names what it refused when it can; the generic line is the fallback.
    const onFailed = (refusalReason?: string | null) =>
      setSubmitError(refusalReason || "Couldn't save those details. Please try again.");

    if (useTokenPath) {
      return updatePlayer({
        variables: { startedAt: sessionStartedAt, token, playerAssessmentId, actions: [action] },
      })
        .then((result: FetchResult) => {
          setSubmitting(false);
          const recorded = findAck(result.data?.recordTokenUserSession, action);

          if (isAppliedAck(recorded)) onSucceeded();
          else onFailed(recorded?.refusalReason);
        })
        .catch((err: ApolloError) => {
          console.error('updatePlayer (public) failed:', err);
          onFailed();
        });
    }

    // Authoritative: scalar fields are fully known from the request, so the
    // writeFragment IS the truth and PROFILE_REFETCH_QUERIES is suppressed.
    const { error, refusalReason } = await recordPlayerEdit(action, {
      authoritative: true,
      optimisticUpdate: (cache) => {
        const id = cache.identify({ __typename: 'Player', _id: player._id });
        const prev = cache.readFragment({ id, fragment: PLAYER_FIELDS });
        cache.writeFragment({ id, fragment: PLAYER_FIELDS, data: { __typename: 'Player', _id: player._id, ...fields } });
        return () => cache.writeFragment({ id, fragment: PLAYER_FIELDS, data: prev });
      },
    });
    setSubmitting(false);
    if (error) onFailed(refusalReason);
    else onSucceeded();
    return undefined;
  }, [player, playerAssessmentId, useTokenPath, sessionStartedAt, token, updatePlayer, recordPlayerEdit]);

  const onRecord = useCallback(async ({ type, value }: { type: PlayerMilestoneType; value: number }) => {
    if (!player) return undefined;
    const recordedAt = new Date().toISOString();
    const action = buildMilestoneAction(player._id, playerAssessmentId, { type, value, recordedAt });
    if (useTokenPath) {
      return recordMilestone({
        variables: { startedAt: sessionStartedAt, token, playerAssessmentId, actions: [action] },
      }).then((result: FetchResult) => {
        // A refused or unsaved milestone throws, matching the parent path below.
        const recorded = findAck(result.data?.recordTokenUserSession, action);
        if (!isAppliedAck(recorded)) throw new Error(recorded?.refusalReason || 'Save failed');
        return undefined;
      });
    }
    // Provisional: the temp _id is replaced by GetPublicEditPlayer refetch
    // (kept on the hook's refetchQueries) when the server-assigned record lands.
    const { error } = await recordPlayerEdit(action, {
      optimisticUpdate: (cache) => {
        const tempId = `temp:${recordedAt}:${type}`;
        const milestone = { __typename: 'PlayerMilestone' as const, _id: tempId, type, value, recordedAt };
        const playerCacheId = cache.identify({ __typename: 'Player', _id: player._id });
        cache.writeFragment({ id: cache.identify(milestone), fragment: PLAYER_MILESTONE_FIELDS, data: milestone });
        cache.modify({
          id: playerCacheId,
          fields: { milestones: (existing = [], { toReference }) => [...existing, toReference(milestone)] },
        });
        return () => cache.modify({
          id: playerCacheId,
          fields: { milestones: (existing = [], { readField }) => existing.filter((ref: any) => readField('_id', ref) !== tempId) },
        });
      },
    });
    // Throw so PlayerMilestoneSection's Promise.all -> catch surfaces failures
    // the same way the token path does (Apollo rejects on error).
    if (error) throw error;
    return undefined;
  }, [player, playerAssessmentId, useTokenPath, sessionStartedAt, token, recordMilestone, recordPlayerEdit]);

  if (!player) return null;

  const initialValues: IPublicProfileFields = {
    firstName: player.firstName || '',
    lastName: player.lastName || '',
    nickname: player.nickname || '',
    date: player.dateOfBirth || '',
    handedness: player.handedness ? { value: player.handedness, label: HANDEDNESS_LABELS[player.handedness] } : null,
  };

  return (
    <PlayerPageCard gutterBottom>
      <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>Player details</Typography>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(form: FormikProps<IPublicProfileFields>) => (
          <Form id={FORM_ID}>
            <FocusFirstInvalidField formId={FORM_ID} />
            <SharedPlayerProfileFields />
            <Box display="flex" alignItems="center" mt={1} style={{ gap: 8 }}>
              <PrimaryButton type="submit" disabled={form.isSubmitting}>
                {form.isSubmitting && <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />}
                {form.isSubmitting ? 'Saving...' : 'Save'}
              </PrimaryButton>
              {submitError && <FormHelperText error>{submitError}</FormHelperText>}
              {showSaved && <FormHelperText className="success">Saved</FormHelperText>}
            </Box>
          </Form>
        )}
      </Formik>
      <Box mt={3}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>Measurements</Typography>
        <PlayerMilestoneSection milestones={player.milestones} onRecord={onRecord} />
      </Box>
    </PlayerPageCard>
  );
};
